import { createRootRoute, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { ClientSidebar, ClientHeader, ClientFooter, MobileSidebarProvider } from '@/components/layout';
import { useRouterState, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useAuth } from '@/features/auth';
import * as authLib from '@/lib/auth';
import { getPortalRedirectTarget } from '@/lib/portal-redirect';
import { NotFoundPage } from '@/components/not-found-page';

// Root layout route - wraps all other routes
export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
});

function RootComponent() {
  const router = useRouterState();
  const navigate = useNavigate();
  const auth = useAuth();
  const pathname = router.location.pathname;
  const showRouterDevtools = import.meta.env.DEV;

  // Handle portal-level redirects (without trailing slash) with role checks
  useEffect(() => {
    const storedUser = authLib.getStoredUser();
    const isAuthenticated = auth?.isAuthenticated || authLib.isAuthenticated();
    const hasRole = (role: string) =>
      Boolean(auth?.hasRole(role) || authLib.hasRole(auth?.user || storedUser, role));

    const redirectTarget = getPortalRedirectTarget({
      pathname,
      isAuthenticated,
      hasRole: (role) => hasRole(role),
    });

    if (redirectTarget && redirectTarget !== pathname) {
      navigate({ to: redirectTarget, replace: true });
    }
  }, [pathname, navigate, auth]);

  // Prevent the window/body from scrolling on full-viewport admin & operations routes; inner layouts scroll instead.
  useEffect(() => {
    const lock =
      pathname.startsWith('/admin') ||
      pathname.startsWith('/operations') ||
      pathname.startsWith('/sales') ||
      pathname.startsWith('/gm');
    const html = document.documentElement;
    if (lock) {
      html.classList.add('portal-scroll-lock');
    } else {
      html.classList.remove('portal-scroll-lock');
    }
    return () => {
      html.classList.remove('portal-scroll-lock');
    };
  }, [pathname]);

  const isLoginPage = pathname === '/login';
  const isUnauthorizedPage = pathname === '/unauthorized';
  const isNotFoundPage = pathname === '/not-found';
  const isUnsubscribePage = pathname === '/unsubscribe';
  const isQuoteDeepLinkPage = pathname.startsWith('/quotes/');
  const isTermsPage = pathname === '/terms';
  const isOperationsPage = pathname.startsWith('/operations');
  const isAdminPage = pathname.startsWith('/admin');
  const isSalesPage = pathname.startsWith('/sales');
  const isGmPage = pathname.startsWith('/gm');
  const isClientPage = pathname.startsWith('/client');

  // Login, Unauthorized, Not Found, deep links, terms, and unsubscribe get no chrome
  if (isLoginPage || isUnauthorizedPage || isNotFoundPage || isQuoteDeepLinkPage || isTermsPage || isUnsubscribePage) {
    return (
      <>
        <Outlet />
        {showRouterDevtools ? <TanStackRouterDevtools position="bottom-right" /> : null}
      </>
    );
  }

  if (isOperationsPage) {
    return (
      <MobileSidebarProvider>
        <>
          <Outlet />
          {showRouterDevtools ? <TanStackRouterDevtools position="bottom-right" /> : null}
        </>
      </MobileSidebarProvider>
    );
  }

  if (isAdminPage || isSalesPage || isGmPage) {
    return (
      <MobileSidebarProvider>
        <>
          <Outlet />
          {showRouterDevtools ? <TanStackRouterDevtools position="bottom-right" /> : null}
        </>
      </MobileSidebarProvider>
    );
  }

  // Client portal: full chrome (sidebar + header + footer)
  if (isClientPage) {
    return (
      <MobileSidebarProvider>
        <div className="flex h-screen w-full overflow-hidden bg-gray-50 dark:bg-gray-950">
          {/* Sidebar */}
          <ClientSidebar />

          {/* Main content area */}
          <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
            {/* Header */}
            <ClientHeader />

            {/* Page content */}
            <main className="flex-1 overflow-y-auto p-6 sm:p-8">
              <Outlet />
            </main>

            {/* Footer */}
            <ClientFooter />

            {/* DevTools */}
            {showRouterDevtools ? <TanStackRouterDevtools position="bottom-right" /> : null}
          </div>
        </div>
      </MobileSidebarProvider>
    );
  }

  // Fallback: unmatched / unknown paths (e.g. typo URLs) render the Outlet
  // chrome-less so the in-place not-found page owns the whole viewport.
  return (
    <>
      <Outlet />
      {showRouterDevtools ? <TanStackRouterDevtools position="bottom-right" /> : null}
    </>
  );
}

// Render the not-found page in place rather than redirecting. A redirect via
// <Navigate> here triggers Chrome's replaceState flood-throttle because the
// router re-renders the notFoundComponent during the in-flight navigation.
// Rendering in place also preserves the URL the user actually typed, which is
// useful both for debugging and so refresh re-shows the same page.
function NotFound() {
  return <NotFoundPage />;
}
